<script setup lang="ts">
import { computed, defineComponent, type VNode } from 'vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Button from 'primevue/button'
import Tag from 'primevue/tag'
import Skeleton from 'primevue/skeleton'

interface ColumnDef {
  field: string
  header: string
  sortable?: boolean
  style?: string
  class?: string
  bodyClass?: string
  /** Oculta la columna por debajo de `md` (768px) para las vistas densas en móvil. */
  hideOnMobile?: boolean
  /**
   * El texto salta de línea en vez de recortarse.
   *
   * Recortar con puntos suspensivos está bien para un dato corto que se reconoce de sobra,
   * y está mal para uno con el que hay que distinguir: dos nombres de cliente recortados no
   * dicen cuál es cuál, y son justo los dos que hay que distinguir. Un corte no es
   * información, es la ausencia de ella con un signo.
   *
   * Con `break-words` el texto usa las líneas que necesite **dentro de su columna**, así que
   * la fila crece pero no se derrama sobre la de al lado.
   */
  wrap?: boolean
  template?: (row: unknown) => unknown
  severity?: (value: string) => 'success' | 'warning' | 'danger' | 'info' | 'secondary'
}

interface RowAction {
  label: string
  value: string
  icon?: string
  severity?: 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'info' | 'contrast'
}

/** Una columna que se queda pegada al borde derecho de la tabla. */
interface StickyColumn {
  field: string
  /** Su ancho. Hace falta para saber dónde empieza la siguiente. */
  width: number
}

interface Props {
  columns: ColumnDef[]
  rows: unknown[]
  loading?: boolean
  paginator?: boolean
  rowsPerPage?: number
  rowsPerPageOptions?: number[]
  sortField?: string
  sortOrder?: number
  onRowClick?: (row: unknown) => void
  onAction?: (row: unknown, action: string) => void
  selectionMode?: 'single' | 'multiple'
  selectedRows?: unknown[]
  /**
   * Columnas que **no se van de la vista**, pegadas al borde derecho.
   *
   * Una tabla con nueve columnas no cabe en una pantalla de portátil y lo que se
   * sale por la derecha es siempre lo que se puso al final. Aquí lo que se puso al
   * final fue lo que más se usa —si está facturado y las acciones—, así que se
   * quedan fijas y las demás se desplazan por debajo. Sin esto hay que desplazar
   * horizontalmente para poder ver si algo está facturado, y no hay ni una pista
   * de que haya algo a la derecha: la tabla aparece cortada.
   *
   * Los anchos son obligatorios y no decorativos: cada columna fija tiene que saber
   * cuánto ocupa la que tiene a su derecha para siar su `right`.
   */
  stickyRight?: StickyColumn[]
  /**
   * Cómo se reparten los anchos.
   *
   * `auto` es el del navegador: una columna crece si su contenido no cabe en el ancho
   * declarado. Eso hace que los anchos no sirvan de nada: se declara 150 para Cliente y
   * el nombre la empuja a 180, y la tabla se sale lo mismo que si no hubiera ancho. Es un
   * fallo silencioso, porque el ancho **parece** estar puesto.
   *
   * `fixed` obliga a que el ancho declarado sea el ancho real, y es lo que hace falta
   * cuando la suma de las columnas tiene que caber en una pantalla. A cambio, el texto
   * largo se recorta: por eso el cuerpo por defecto recorta cuando el reparto es fijo.
   */
  layout?: 'auto' | 'fixed'
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'update:sortField': [field: string | undefined]
  'update:sortOrder': [order: number]
  'update:selectedRows': [rows: unknown[]]
  rowClick: [row: unknown]
  action: [row: unknown, action: string]
  page: [page: number]
}>()

const defaultPageSize = props.rowsPerPage ?? 10
const pageSizeOptions = props.rowsPerPageOptions ?? [10, 25, 50, 100]

/**
 * Ancho de la columna de acciones, en píxeles.
 *
 * Sale de la cuenta de lo que hay dentro: cuatro botones cuadrados de `1.75rem` con
 * `gap-0.5` son `112 + 6 = 118px`, y quedan cinco a cada lado para que no se vea pegada al
 * borde de la celda.
 *
 * Va escrito dos veces —aquí y en el `style` de la columna— y hay un test que lo
 * comprueba. Si divergen, la última del bloque se solapa con la anterior, y eso no se
 * nota hasta que se desplaza la tabla.
 */
const ANCHO_ACCIONES = 128

/**
 * La columna de acciones es sintética: no está en `columns` porque su contenido
 * depende de la fila (`row.actions`), no de un campo. Se muestra solo si alguna
 * fila trae acciones, para no dejar una columna vacía.
 */
const hasActions = computed(() =>
  props.rows.some((row) => Array.isArray((row as { actions?: unknown } | undefined)?.actions)),
)
const totalColumns = computed(() => props.columns.length + (hasActions.value ? 1 : 0))

const fijadas = computed<StickyColumn[]>(() => props.stickyRight ?? [])

/**
 * Si hay columnas fijas, la de acciones también se queda.
 *
 * Es la última de la fila y la que más se usa: una columna fija que dejara hueca la
 * columna contigua sería un sitio perdido en el borde derecho.
 */
const fijanAcciones = computed(() => fijadas.value.length > 0 && hasActions.value)

/** Cuánto se separa del borde derecho la columna `field`, en píxeles. */
function separacionDerecha(field: string): number | null {
  const indice = fijadas.value.findIndex((columna) => columna.field === field)

  if (indice === -1) return null

  let pixeles = fijanAcciones.value ? ANCHO_ACCIONES : 0

  // Las de la derecha van sumando: cada una arranca donde termina la anterior.
  for (let i = indice + 1; i < fijadas.value.length; i += 1) {
    pixeles += fijadas.value[i].width
  }

  return pixeles
}

function estiloFijo(field: string): Record<string, string> | undefined {
  const separacion = separacionDerecha(field)

  if (separacion === null) return undefined

  return { position: 'sticky', right: `${separacion}px` }
}

function clasesFijas(field: string): string | undefined {
  const indice = fijadas.value.findIndex((columna) => columna.field === field)

  if (indice === -1) return undefined

  // La sombra va solo en la primera del grupo —la más a la izquierda—, porque es el
  // borde del bloque entero. Ponerla en todas deja tres líneas cruzando la fila.
  return indice === 0 ? 'crm-col-fijada crm-col-fijada-borde' : 'crm-col-fijada'
}

/** Las acciones son la última columna: se pegan al borde, sin separación. */
const estiloDeAcciones: Record<string, string> = { position: 'sticky', right: '0px' }

/**
 * Las clases de la `<table>` de verdad.
 *
 * Van por `pt` y no por `class`: el `class` del `DataTable` cae en su envoltorio, que es
 * un `div` de scroll, no en la tabla. Poner `table-fixed` ahí fue lo primero que se
 * probó y no hacía nada: la clase estaba en el sitio y la tabla seguía repartiendo sola.
 */
const clasesDeTabla = computed(() => (props.layout === 'fixed' ? 'w-full table-fixed' : 'w-full'))

/**
 * Las piezas de la propia tabla.
 *
 * PrimeVue marca **todo** lo que pinta con `data-pc-name`, y el `data-pc-name` de una celda
 * es `bodycell`, el de una cabecera `headercell` y el de la raíz `datatable`. Están en el
 * camino de cualquier clic de la fila, así que si se contaran como control la fila dejaría
 * de responder **entera**, no solo en los botones.
 */
const PIEZAS_DE_LA_TABLA = [
  'datatable',
  'bodycell',
  'headercell',
  'row',
  'tablebody',
  'tableheader',
  'paginator',
]

/**
 * Si el clic ha caído sobre un control, y no sobre la fila.
 *
 * Los controles de PrimeVue **no son** `<select>` ni `<input>`: son `<div>`. El desplegable
 * de estado de la fila es un `div.p-select`, así que un guard que buscara solo etiquetas
 * nativas no lo encontraba, el clic seguía subiendo hasta la fila y abría la cotización
 * encima. Y como no fallaba a medias —se veía abrir el desplegable un instante y luego la
 * ficha— parecía un desplegable que a veces no responde.
 *
 * El marcador es `data-pc-name`, que es donde va el nombre del componente: `select`,
 * `datepicker`, `inputnumber`… Con él, el siguiente control que entre en una celda ya queda
 * cubierto sin tocar nada.
 *
 * Ojo con `data-p`: ese atributo existe pero **va vacío** en las raíces de PrimeVue y no
 * sirve para distinguir nada. Es el que se confunde al leer el HTML.
 *
 * `<a>` e `<input>` se sueltan aparte porque existen sin marca de PrimeVue.
 */
function pulsaSobreUnControl(target: HTMLElement | null): boolean {
  if (target === null) return false
  if (target.closest('a, input, select, textarea')) return true

  const marcado = target.closest('[data-pc-name]')

  if (marcado === null) return false

  return !PIEZAS_DE_LA_TABLA.includes(marcado.getAttribute('data-pc-name') ?? '')
}

/** PrimeVue emite `{ data, index, originalEvent }`. */
function handleRowClick(payload: { data: unknown; originalEvent: Event }): void {
  const target = payload.originalEvent?.target as HTMLElement | null
  if (pulsaSobreUnControl(target)) return
  emit('rowClick', payload.data)
  props.onRowClick?.(payload.data)
}

function handleAction(row: unknown, action: string): void {
  emit('action', row, action)
  props.onAction?.(row, action)
}

function actionsFor(row: unknown): RowAction[] {
  const actions = (row as Record<string, unknown> | undefined)?.['actions']
  return Array.isArray(actions) ? (actions as RowAction[]) : []
}

/**
 * Renderizador de celda.
 *
 * Existe porque `<component :is="funcion">` invoca la función como **componente
 * funcional**, y eso significa que recibe `(props, context)`: el primer argumento
 * son las props, no la fila. Una plantilla declarada como `(row) => VNode` se
 * comía entonces `{ row, field }` como si fuera la fila, y `row.customer_name`
 * salía `undefined` sin ningún error: la celda quedaba en blanco.
 *
 * Este envoltorio llama a la plantilla con la fila, que es lo que la prop
 * promete. Es el mismo motivo por el que `Column` de PrimeVue no se anida dentro
 * de un `ColumnGroup`.
 */
const CellTemplate = defineComponent({
  name: 'DenseTableCell',
  props: {
    render: { type: Function as unknown as () => (row: unknown) => unknown, required: true },
    row: { type: null as unknown as () => unknown, required: true },
  },
  setup(props) {
    // Se llama a la plantilla directamente y se devuelve su VNode. Pasarla por
    // `h()` sería volver al camino de componente funcional que causaba el
    // problema: aquí lo único que se hace es invocar la función con la fila.
    //
    // La plantilla tiene que devolver **etiquetas**, no componentes. Si devuelve un
    // `RouterLink` o un `Button`, se rehace en cada render y eso es montar y desmontar
    // el componente sin parar: con un `RouterLink` dentro de una celda sale un «Maximum
    // recursive updates exceeded» que tumba la vista entera. Con texto o etiquetas sueltas
    // no se nota, porque un VNode de etiqueta no tiene nada que montar — y por eso el
    // fallo se cuela en el sitio con más filas y más datos.
    return () => (props.render as (row: unknown) => unknown)(props.row) as VNode
  },
})

/** Tailwind no detecta clases construidas en runtime, por eso van literales. */
function visibilityClass(col: ColumnDef): string {
  return col.hideOnMobile ? 'hidden md:table-cell' : ''
}

/**
 * Cómo va el texto de una celda de texto.
 *
 * Con reparto fijo el ancho de la columna manda, así que hay que decir qué se hace con el
 * texto que no cabe: salta de línea (`wrap`) o se recorta con puntos.
 */
function claseDelCuerpo(col: ColumnDef): string {
  if (props.layout !== 'fixed') return ''

  return col.wrap ? 'block break-words' : 'block truncate'
}

function onSort(event: {
  sortField: string | ((item: unknown) => string) | undefined
  sortOrder: number | null | undefined
}): void {
  emit('update:sortField', typeof event.sortField === 'function' ? undefined : event.sortField)
  emit('update:sortOrder', event.sortOrder ?? 0)
}

function onSelectionChange(event: { value: unknown[] }): void {
  emit('update:selectedRows', event.value)
}

function onPage(event: { page: number }): void {
  emit('page', event.page)
}
</script>

<template>
  <div class="overflow-x-auto">
    <!--
      PrimeVue 4 registra las columnas con un `HelperSet` que solo recorre los
      hijos directos del slot por defecto de `DataTable`. Anidar las columnas
      dentro de `ColumnGroup`/`Row` hacía que `DataTable.columns` quedara
      `undefined` y el cuerpo de la tabla salía vacío, así que aquí las columnas
      van planas y el texto de cabecera se pasa por la prop `header`
      (`HeaderCell` ignora el slot por defecto de `Column`).
    -->
    <DataTable
      :value="props.rows"
      :loading="props.loading"
      :paginator="props.paginator ?? true"
      :rows="defaultPageSize"
      :rows-per-page-options="pageSizeOptions"
      :sort-field="props.sortField"
      :sort-order="props.sortOrder"
      row-hover
      :selection-mode="props.selectionMode"
      :selection="props.selectedRows"
      data-key="id"
      class="crm-table p-datatable-striped p-datatable-sm"
      :pt="{ table: { class: clasesDeTabla } }"
      @sort="onSort"
      @row-click="handleRowClick"
      @selection-change="onSelectionChange"
      @page="onPage"
    >
      <Column
        v-for="col in props.columns"
        :key="col.field"
        :field="col.field"
        :header="col.header"
        :sortable="col.sortable"
        :style="col.style"
        :class="[col.class, visibilityClass(col)]"
        :body-class="clasesFijas(col.field)"
        :header-class="clasesFijas(col.field)"
        :body-style="estiloFijo(col.field)"
        :header-style="estiloFijo(col.field)"
      >
        <template #body="slotProps">
          <CellTemplate
            v-if="col.template"
            :render="col.template"
            :row="slotProps.data"
          />
          <Tag
            v-else-if="col.severity"
            :value="slotProps.data[col.field]"
            :severity="col.severity(slotProps.data[col.field])"
          />
          <span
            v-else
            :class="claseDelCuerpo(col)"
          >{{ slotProps.data[col.field] ?? '—' }}</span>
        </template>
      </Column>

      <!--
        Los botones van solo con el icono. Con icono **y** texto el texto no aporta
        nada al lector de pantalla —el `aria-label` ya lo dice— y ocupa el ancho de una
        columna que solo necesita dos iconos.

        **Sin `!p-2`.** El botón con solo icono ya trae su propia caja cuadrada:
        `.p-button-icon-only` pone `width: 2rem`, `padding-inline: 0` y `height: 2rem`
        cuando es redondo. Forzar el relleno con `!important` le añadía ocho píxeles por
        cada lado **dentro de una caja que ya tenía el ancho justo**, así que el icono
        dejaba de estar en el centro y la fila de iconos quedaba desalineada respecto a
        la columna. El `center` de la celda tampoco lo arregla: centra la celda, y la
        celda estaba bien; lo descentrado era el botón por dentro.

        El relleno se deja al tema, que es quien sabe la medida que quiere. Un
        `!important` encima de una caja ya dimensionada solo puede empeorarla.

        El relleno de la celda también se quita (`!px-0`): cuatro botones de `2rem` con
        `gap-1` ocupan `140px`, y con el relleno de la celda no cabían en la columna y la
        última se salía. El ancho sale de la cuenta, no de un número redondo:

            4 botones x 2rem + 3 huecos x 0.125rem = 128 + 6 = 134px
            ANCHO_ACCIONES = 134px, con 3px de margen a cada lado
      -->
      <Column
        v-if="hasActions"
        header="Acciones"
        :style="`width: ${ANCHO_ACCIONES}px`"
        :body-class="[fijanAcciones ? 'crm-col-fijada' : '', '!px-0 text-center'].join(' ')"
        :header-class="fijanAcciones ? 'crm-col-fijada' : undefined"
        :body-style="fijanAcciones ? estiloDeAcciones : undefined"
        :header-style="fijanAcciones ? estiloDeAcciones : undefined"
      >
        <template #body="slotProps">
          <div class="flex items-center justify-center gap-0.5">
            <Button
              v-for="action in actionsFor(slotProps.data)"
              :key="action.value"
              :icon="action.icon"
              :severity="action.severity ?? 'secondary'"
              size="small"
              rounded
              :aria-label="action.label"
              :title="action.label"
              outlined
              class="!size-7 !p-0"
              @click="handleAction(slotProps.data, action.value)"
            />
          </div>
        </template>
      </Column>

      <template #empty>
        <div class="text-center py-8">
          <i class="pi pi-inbox text-4xl text-surface-300 dark:text-surface-600 mb-3" />
          <p class="text-surface-500 dark:text-surface-400">
            No hay datos disponibles
          </p>
        </div>
      </template>

      <template #loading>
        <tr>
          <td
            :colspan="totalColumns"
            class="p-4"
          >
            <div class="flex justify-center gap-2">
              <Skeleton
                v-for="i in 3"
                :key="i"
                width="2rem"
                height="2rem"
                class="rounded"
              />
            </div>
          </td>
        </tr>
      </template>
    </DataTable>
  </div>
</template>

<style scoped>
/*
| El relleno de las celdas
|--------------------------------------------------------------------------
|
| Cuatro píxeles por arriba y por abajo, ocho de lado.
|
| Antes eran ocho por arriba y ocho por abajo: dieciséis por fila, que con nueve columnas
| es lo que hace que una tabla se coma media pantalla antes de enseñar un dato. El relleno
| de lado baja a ocho por la misma razón: cada columna añade sixteen píxeles de hueco, y con
| nueve son más de un dedo de ancho tirado a la nada entre el texto y la siguiente celda.
|
| Se escribe aquí y no en la hoja global porque el ajuste es de esta tabla: `DenseTable`
| existe para las listas densas, y `p-datatable-sm` del tema trae un relleno pensado para
| tablas normales.
*/
@media (min-width: 768px) {
  :deep(.p-datatable-sm .p-datatable-thead > tr > th) {
    padding: 0.25rem 0.5rem;
    font-size: 0.75rem;
  }
  :deep(.p-datatable-sm .p-datatable-tbody > tr > td) {
    padding: 0.25rem 0.5rem;
    font-size: 0.875rem;
  }
}

@media (max-width: 767px) {
  :deep(.p-datatable .p-button) {
    min-height: 44px;
    min-width: 44px;
  }

  .overflow-x-auto {
    -webkit-overflow-scrolling: touch;
  }
}
</style>